import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Briefcase,
  Calendar,
  Users,
  Activity,
  ListChecks,
  GanttChart,
  Map,
  Trash2,
  UserPlus,
  Tag,
  Clock,
  Edit2,
  Check,
  Pencil,
  X,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import ProjectMembersDialog from './ProjectMembersDialog';
import ProjectKanbanTab from './ProjectKanbanTab';
import ProjectGanttTab from './ProjectGanttTab';
import ProjectMapTab from './ProjectMapTab';
import ProjectAIPanel from './ProjectAIPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'active', label: 'Aktiv', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'on_hold', label: 'Gözləyir', color: 'bg-amber-100 text-amber-800' },
  { value: 'completed', label: 'Tamamlandı', color: 'bg-blue-100 text-blue-800' },
  { value: 'archived', label: 'Arxiv', color: 'bg-zinc-100 text-zinc-600' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Aşağı', color: 'text-zinc-600' },
  { value: 'medium', label: 'Orta', color: 'text-amber-600' },
  { value: 'high', label: 'Yüksək', color: 'text-red-600' },
];

const ROLE_LABELS = {
  superadmin: 'SuperAdmin',
  admin: 'Admin',
  operator: 'Operator',
  technician: 'Texnik',
  accountant: 'Mühasib',
  d2d_agent: 'D2D Agent',
  lawyer: 'Hüquqşunas',
  network_admin: 'NetAdmin',
  system_admin: 'SysAdmin',
};

const ACTION_LABELS = {
  created: { label: 'Yaratdı', color: 'bg-emerald-50 text-emerald-700' },
  updated: { label: 'Redaktə etdi', color: 'bg-blue-50 text-blue-700' },
  members_added: { label: 'Üzv əlavə etdi', color: 'bg-purple-50 text-purple-700' },
  member_removed: { label: 'Üzv sildi', color: 'bg-orange-50 text-orange-700' },
  archived: { label: 'Arxivləşdirdi', color: 'bg-zinc-100 text-zinc-700' },
  item_created: { label: 'Mərhələ yaratdı', color: 'bg-emerald-50 text-emerald-700' },
  item_updated: { label: 'Mərhələni redaktə etdi', color: 'bg-blue-50 text-blue-700' },
  item_moved: { label: 'Mərhələni köçürdü', color: 'bg-purple-50 text-purple-700' },
  item_deleted: { label: 'Mərhələni sildi', color: 'bg-red-50 text-red-700' },
  geo_imported: { label: 'KMZ/KML idxal etdi', color: 'bg-teal-50 text-teal-700' },
  geo_created: { label: 'Geo element yaratdı', color: 'bg-emerald-50 text-emerald-700' },
  geo_updated: { label: 'Geo elementi redaktə etdi', color: 'bg-blue-50 text-blue-700' },
  geo_bulk_deleted: { label: 'Geo elementlər sildi', color: 'bg-orange-50 text-orange-700' },
  ai_wizard_generated: { label: '🪄 AI Layihə Wizard işlətdi', color: 'bg-purple-50 text-purple-700' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'indi';
  if (min < 60) return `${min} dəq əvvəl`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} saat əvvəl`;
  const day = Math.floor(hr / 24);
  return `${day} gün əvvəl`;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('az-AZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export default function ProjectDetailModal({ open, project, user, onClose, onChange }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [current, setCurrent] = useState(project);
  const [members, setMembers] = useState({ direct_users: [], groups: [], effective_count: 0 });
  const [activity, setActivity] = useState([]);
  const [stats, setStats] = useState(null);
  const [polygons, setPolygons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showAddMembers, setShowAddMembers] = useState(false);

  const canEdit =
    user?.is_superadmin ||
    user?.role === 'superadmin' ||
    user?.role === 'admin' ||
    current?.owner_id === user?.id;

  const fetchAll = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const [projRes, memRes, actRes, statsRes, geoRes] = await Promise.all([
        axios.get(`${API}/projects/${project.id}`, { headers }),
        axios.get(`${API}/projects/${project.id}/members`, { headers }),
        axios.get(`${API}/projects/${project.id}/activity`, { headers }),
        axios.get(`${API}/projects/${project.id}/stats`, { headers }),
        axios.get(`${API}/projects/${project.id}/geo?geometry_type=Polygon`, { headers }).catch(() => ({ data: [] })),
      ]);
      setCurrent(projRes.data);
      setMembers(memRes.data);
      setActivity(actRes.data || []);
      setStats(statsRes.data);
      setPolygons(geoRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Məlumat yüklənmədi');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  useEffect(() => {
    if (open) {
      setCurrent(project);
      setEditMode(false);
      fetchAll();
    }
  }, [open, project, fetchAll]);

  const startEdit = () => {
    setEditForm({
      name: current.name,
      description: current.description || '',
      status: current.status,
      priority: current.priority,
      start_date: current.start_date || '',
      due_date: current.due_date || '',
    });
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditForm({});
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/projects/${current.id}`, editForm, { headers });
      toast.success('Yadda saxlanıldı');
      setEditMode(false);
      await fetchAll();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Saxlanılmadı');
    }
  };

  const handleRemoveMember = async (memberId, type, label) => {
    if (!window.confirm(`"${label}" silinsin?`)) return;
    try {
      await axios.delete(
        `${API}/projects/${current.id}/members/${memberId}?type=${type}`,
        { headers }
      );
      toast.success('Silindi');
      await fetchAll();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Silinmədi');
    }
  };

  const statusInfo = STATUS_OPTIONS.find((s) => s.value === current?.status);
  const priorityInfo = PRIORITY_OPTIONS.find((p) => p.value === current?.priority);

  if (!current) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent
          className="max-w-6xl max-h-[90vh] overflow-y-auto p-0"
          data-testid="project-detail-modal"
        >
          <DialogTitle className="sr-only">{current.name || 'Layihə təfərrüatları'}</DialogTitle>
          <DialogDescription className="sr-only">
            Layihənin təfərrüat paneli: ümumi məlumat, üzvlər, aktivlik, mərhələlər, Gantt və xəritə.
          </DialogDescription>
          {/* Header */}
          <div
            className="px-6 py-5 border-b"
            style={{ borderLeftWidth: 5, borderLeftColor: current.color || '#9333ea' }}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                {editMode ? (
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="text-xl font-bold"
                    data-testid="edit-project-name"
                  />
                ) : (
                  <h2 className="text-2xl font-bold">{current.name}</h2>
                )}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <Badge className={statusInfo?.color}>{statusInfo?.label}</Badge>
                  <span className={`text-sm font-medium ${priorityInfo?.color}`}>
                    ● {priorityInfo?.label}
                  </span>
                  {current.created_at && (
                    <span className="text-xs text-zinc-500 ml-2">
                      Yaradılıb: {formatDate(current.created_at)}
                    </span>
                  )}
                </div>
              </div>
              {canEdit && (
                <div className="flex gap-2">
                  {editMode ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={cancelEdit}
                        data-testid="cancel-edit-btn"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={saveEdit}
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid="save-edit-btn"
                      >
                        <Check className="h-4 w-4 mr-1" /> Saxla
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={startEdit}
                      data-testid="edit-project-btn"
                    >
                      <Pencil className="h-4 w-4 mr-1" /> Redaktə et
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Stats bar */}
          {stats && (
            <div className="grid grid-cols-4 border-b">
              <div className="px-6 py-3 border-r">
                <div className="text-xs text-zinc-500">İrəliləyiş</div>
                <div className="text-xl font-semibold">{stats.progress_pct}%</div>
              </div>
              <div className="px-6 py-3 border-r">
                <div className="text-xs text-zinc-500">Tapşırıqlar</div>
                <div className="text-xl font-semibold">
                  {stats.done_tasks}/{stats.total_tasks}
                </div>
              </div>
              <div className="px-6 py-3 border-r">
                <div className="text-xs text-zinc-500">Vaxtı keçmiş</div>
                <div
                  className={`text-xl font-semibold ${
                    stats.overdue_count > 0 ? 'text-red-600' : ''
                  }`}
                >
                  {stats.overdue_count}
                </div>
              </div>
              <div className="px-6 py-3">
                <div className="text-xs text-zinc-500">Üzvlər</div>
                <div className="text-xl font-semibold">{stats.member_count}</div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <Tabs defaultValue="overview" className="px-6 pt-4 pb-6">
            <TabsList>
              <TabsTrigger value="overview" data-testid="tab-overview">
                <Briefcase className="h-4 w-4 mr-1.5" /> Ümumi
              </TabsTrigger>
              <TabsTrigger value="members" data-testid="tab-members">
                <Users className="h-4 w-4 mr-1.5" /> Üzvlər ({members.effective_count})
              </TabsTrigger>
              <TabsTrigger value="activity" data-testid="tab-activity">
                <Activity className="h-4 w-4 mr-1.5" /> Aktivlik ({activity.length})
              </TabsTrigger>
              <TabsTrigger value="tasks" data-testid="tab-tasks">
                <ListChecks className="h-4 w-4 mr-1.5" /> Mərhələlər
              </TabsTrigger>
              <TabsTrigger value="gantt" data-testid="tab-gantt">
                <GanttChart className="h-4 w-4 mr-1.5" /> Gantt
              </TabsTrigger>
              <TabsTrigger value="map" data-testid="tab-map">
                <Map className="h-4 w-4 mr-1.5" /> Xəritə
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="mt-4 space-y-4">
              {/* AI Helper Panel (Sprint 8-9) */}
              <ProjectAIPanel
                project={current}
                polygons={polygons}
                onItemsCreated={fetchAll}
              />

              <div>
                <div className="text-sm font-medium text-zinc-700 mb-1">Açıqlama</div>
                {editMode ? (
                  <Textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    rows={4}
                    data-testid="edit-description"
                  />
                ) : (
                  <div className="text-sm text-zinc-600 whitespace-pre-wrap">
                    {current.description || <span className="italic text-zinc-400">Açıqlama yoxdur</span>}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-xs text-zinc-500 mb-1">Status</div>
                  {editMode ? (
                    <Select
                      value={editForm.status}
                      onValueChange={(v) => setEditForm({ ...editForm, status: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge className={statusInfo?.color}>{statusInfo?.label}</Badge>
                  )}
                </div>
                <div>
                  <div className="text-xs text-zinc-500 mb-1">Prioritet</div>
                  {editMode ? (
                    <Select
                      value={editForm.priority}
                      onValueChange={(v) => setEditForm({ ...editForm, priority: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITY_OPTIONS.map((p) => (
                          <SelectItem key={p.value} value={p.value}>
                            {p.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className={`text-sm font-semibold ${priorityInfo?.color}`}>
                      {priorityInfo?.label}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs text-zinc-500 mb-1 flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> Başlanma
                  </div>
                  {editMode ? (
                    <Input
                      type="date"
                      value={editForm.start_date}
                      onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                    />
                  ) : (
                    <div className="text-sm">{formatDate(current.start_date)}</div>
                  )}
                </div>
                <div>
                  <div className="text-xs text-zinc-500 mb-1 flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> Bitmə
                  </div>
                  {editMode ? (
                    <Input
                      type="date"
                      value={editForm.due_date}
                      onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                    />
                  ) : (
                    <div className="text-sm">{formatDate(current.due_date)}</div>
                  )}
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="text-sm font-medium text-zinc-700 mb-2">Status üzrə tapşırıqlar</div>
                {stats?.by_status && Object.keys(stats.by_status).length > 0 ? (
                  <div className="flex gap-2 flex-wrap">
                    {Object.entries(stats.by_status).map(([k, v]) => (
                      <Badge key={k} variant="secondary">
                        {k}: {v}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-zinc-400 italic">Hələ tapşırıq yoxdur</div>
                )}
              </div>
            </TabsContent>

            {/* Members Tab */}
            <TabsContent value="members" className="mt-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-zinc-600">
                  Birbaşa: {members.direct_users.length} • Qruplar: {members.groups.length} •{' '}
                  <strong>Effektiv: {members.effective_count}</strong>
                </div>
                {canEdit && (
                  <Button
                    size="sm"
                    onClick={() => setShowAddMembers(true)}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="add-members-btn"
                  >
                    <UserPlus className="h-4 w-4 mr-1" /> Üzv əlavə et
                  </Button>
                )}
              </div>

              {/* Groups */}
              {members.groups.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-zinc-500 mb-2 flex items-center gap-1.5">
                    <Tag className="h-3 w-3" /> Qruplar
                  </div>
                  <div className="space-y-1">
                    {members.groups.map((g) => (
                      <div
                        key={g.id}
                        className="flex items-center justify-between border rounded-md px-3 py-2 bg-zinc-50"
                        data-testid={`group-member-${g.id}`}
                      >
                        <div className="text-sm font-medium">{g.name}</div>
                        {canEdit && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-zinc-400 hover:text-red-600"
                            onClick={() => handleRemoveMember(g.id, 'group', g.name)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Users */}
              <div>
                <div className="text-xs font-medium text-zinc-500 mb-2 flex items-center gap-1.5">
                  <Users className="h-3 w-3" /> İstifadəçilər (effektiv)
                </div>
                {members.direct_users.length === 0 ? (
                  <div className="text-sm text-zinc-400 italic py-3 text-center">
                    Üzv yoxdur
                  </div>
                ) : (
                  <div className="space-y-1">
                    {members.direct_users.map((u) => {
                      const isDirect = (current.member_ids || []).includes(u.id);
                      const isOwner = current.owner_id === u.id;
                      return (
                        <div
                          key={u.id}
                          className="flex items-center justify-between border rounded-md px-3 py-2 hover:bg-zinc-50"
                          data-testid={`user-member-${u.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate flex items-center gap-2">
                              {u.full_name || u.username}
                              {isOwner && (
                                <Badge variant="outline" className="text-xs border-purple-300 text-purple-700">
                                  Owner
                                </Badge>
                              )}
                              {!isDirect && !isOwner && (
                                <Badge variant="outline" className="text-xs">
                                  qrupdan
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs text-zinc-500 mt-0.5">
                              @{u.username} • <Badge variant="secondary" className="text-xs">{ROLE_LABELS[u.role] || u.role}</Badge>
                            </div>
                          </div>
                          {canEdit && isDirect && !isOwner && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-zinc-400 hover:text-red-600"
                              onClick={() => handleRemoveMember(u.id, 'user', u.username)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Activity Tab */}
            <TabsContent value="activity" className="mt-4">
              {loading && (
                <div className="text-sm text-zinc-500 py-4">Yüklənir...</div>
              )}
              {!loading && activity.length === 0 && (
                <div className="text-sm text-zinc-400 italic py-8 text-center">
                  Aktivlik tarixçəsi boşdur
                </div>
              )}
              <div className="space-y-2">
                {activity.map((a) => {
                  const info = ACTION_LABELS[a.action] || {
                    label: a.action,
                    color: 'bg-zinc-100 text-zinc-700',
                  };
                  return (
                    <div
                      key={a.id}
                      className="flex items-start gap-3 px-3 py-2 hover:bg-zinc-50 rounded-md"
                      data-testid={`activity-${a.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm">
                          <span className="font-semibold">{a.user_name}</span>{' '}
                          <Badge className={`${info.color} text-xs ml-1`}>{info.label}</Badge>
                        </div>
                        {a.details && Object.keys(a.details).length > 0 && (
                          <div className="text-xs text-zinc-500 mt-0.5">
                            {Object.entries(a.details)
                              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.length + ' element' : v}`)
                              .join(' • ')}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-zinc-400 whitespace-nowrap flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {timeAgo(a.created_at)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </TabsContent>

            {/* Tasks/Kanban Tab */}
            <TabsContent value="tasks" className="mt-4">
              <ProjectKanbanTab
                project={current}
                user={user}
                members={members.direct_users || []}
                canEdit={canEdit}
                onItemsChanged={fetchAll}
              />
            </TabsContent>

            {/* Gantt Tab */}
            <TabsContent value="gantt" className="mt-4">
              <ProjectGanttTab
                project={current}
                members={members.direct_users || []}
                canEdit={canEdit}
                onItemsChanged={fetchAll}
              />
            </TabsContent>

            {/* Map / Geo Tab */}
            <TabsContent value="map" className="mt-4">
              <ProjectMapTab
                project={current}
                canEdit={canEdit}
                onChanged={fetchAll}
              />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Add Members Dialog */}
      <ProjectMembersDialog
        open={showAddMembers}
        onClose={() => setShowAddMembers(false)}
        project={current}
        onSaved={() => {
          fetchAll();
          onChange?.();
        }}
      />
    </>
  );
}
